import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import '../../assets/styles/tour.css';

/**
 * Guía de uso interactiva: resalta una parte de la pantalla y explica qué se hace ahí, paso a paso.
 * Los pasos cuyo elemento no existe en pantalla (por ejemplo, una opción que el usuario no tiene) se saltan solos.
 */
export interface TourStep {
    key: string;
    target?: string;            // selector CSS del elemento a resaltar; sin él, el paso se muestra centrado
    area?: 'sidebar' | 'topbar' | 'content';   // dónde está el elemento (en móvil, el menú se abre o cierra según esto)
    icon?: string;
    title: string;
    content: string;
}

interface Props {
    open: boolean;
    steps: TourStep[];
    onAreaChange?: (area: TourStep['area']) => void;
    onFinish: () => void;       // terminó la guía o el usuario la omitió
}

const PAD = 6;          // margen del resaltado alrededor del elemento
const GAP = 14;         // separación entre el resaltado y la tarjeta (ahí va la flecha)
const MARGIN = 16;      // margen mínimo con el borde de la pantalla
const ARROW_INSET = 18; // distancia mínima de la flecha a las esquinas de la tarjeta

// Lado del elemento donde queda la tarjeta; la flecha sale del lado opuesto de la tarjeta, hacia el elemento
type Placement = 'right' | 'bottom' | 'top' | 'left' | 'center';

interface CardPosition {
    top: number;
    left: number;
    placement: Placement;
    arrow: number;      // posición de la flecha a lo largo del borde de la tarjeta (px)
}

// El paso se muestra solo si su elemento está en pantalla (no existe u oculto con display: none, se salta)
export const isStepAvailable = (step: TourStep) => {
    if (!step.target) return true;
    const element = document.querySelector(step.target);
    return !!element && element.getClientRects().length > 0;
};

// ---------- Una guía a la vez: la de una pantalla espera a que se cierre la general (o la de otra pantalla)
let activeTours = 0;
const closedListeners = new Set<() => void>();

/** Ejecuta `callback` cuando no hay ninguna guía abierta; devuelve una función para cancelar la espera. */
export const whenNoTourActive = (callback: () => void) => {
    if (activeTours === 0) {
        callback();
        return () => { };
    }
    const listener = () => {
        if (activeTours > 0) return;
        closedListeners.delete(listener);
        callback();
    };
    closedListeners.add(listener);
    return () => { closedListeners.delete(listener); };
};

const findTarget = (step?: TourStep) => (step?.target ? document.querySelector<HTMLElement>(step.target) : null);

const sameRect = (a: DOMRect | null, b: DOMRect | null) =>
    a === b || (!!a && !!b && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height);

export const GuidedTour = ({ open, steps, onAreaChange, onFinish }: Props) => {
    const [index, setIndex] = useState(0);
    const [rect, setRect] = useState<DOMRect | null>(null);
    const [cardPos, setCardPos] = useState<CardPosition | null>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    const step = steps[index];

    useEffect(() => { if (open) setIndex(0); }, [open]);

    useEffect(() => {
        if (!open) return;
        activeTours++;
        return () => {
            activeTours--;
            // Se avisa después de que la guía termine de cerrarse
            setTimeout(() => Array.from(closedListeners).forEach(listener => listener()), 0);
        };
    }, [open]);

    // Avisa al menú en qué zona está el paso (en móvil, el menú lateral se abre solo para sus pasos)
    useEffect(() => {
        if (open && step) onAreaChange?.(step.area);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, step?.key]);

    // Lleva el elemento a la vista (por ejemplo, una opción al final del menú)
    useEffect(() => {
        if (!open) return;
        const timer = setTimeout(() => findTarget(step)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 60);
        return () => clearTimeout(timer);
    }, [open, step]);

    // Sigue la posición del elemento mientras la guía está abierta (animaciones del menú, desplazamiento, cambio de tamaño)
    useEffect(() => {
        if (!open) return;
        let frame = 0;
        let last: DOMRect | null = null;
        const tick = () => {
            const element = findTarget(step);
            const next = element ? element.getBoundingClientRect() : null;
            if (!sameRect(last, next)) {
                last = next;
                setRect(next && next.width > 0 && next.height > 0 ? next : null);
            }
            frame = requestAnimationFrame(tick);
        };
        tick();
        return () => cancelAnimationFrame(frame);
    }, [open, step]);

    // Ubica la tarjeta junto al elemento: a la derecha, abajo, arriba o a la izquierda, según el espacio libre
    useLayoutEffect(() => {
        if (!open || !cardRef.current) return;
        const card = cardRef.current.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));

        const center: CardPosition = { top: (vh - card.height) / 2, left: (vw - card.width) / 2, placement: 'center', arrow: 0 };
        if (!rect) {
            setCardPos(center);
            return;
        }
        // La tarjeta se centra frente al elemento (sin salirse de la pantalla) y la flecha apunta al centro del elemento
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const top = clamp(centerY - card.height / 2, MARGIN, vh - card.height - MARGIN);
        const left = clamp(centerX - card.width / 2, MARGIN, vw - card.width - MARGIN);
        const arrowY = clamp(centerY - top, ARROW_INSET, card.height - ARROW_INSET);
        const arrowX = clamp(centerX - left, ARROW_INSET, card.width - ARROW_INSET);

        if (rect.right + PAD + GAP + card.width + MARGIN <= vw)
            setCardPos({ top, left: rect.right + PAD + GAP, placement: 'right', arrow: arrowY });
        else if (rect.bottom + PAD + GAP + card.height + MARGIN <= vh)
            setCardPos({ top: rect.bottom + PAD + GAP, left, placement: 'bottom', arrow: arrowX });
        else if (rect.top - PAD - GAP - card.height - MARGIN >= 0)
            setCardPos({ top: rect.top - PAD - GAP - card.height, left, placement: 'top', arrow: arrowX });
        else if (rect.left - PAD - GAP - card.width - MARGIN >= 0)
            setCardPos({ top, left: rect.left - PAD - GAP - card.width, placement: 'left', arrow: arrowY });
        else
            setCardPos({ ...center, top: vh - card.height - MARGIN });
    }, [open, rect, index]);

    const next = useCallback(() => {
        if (index >= steps.length - 1) onFinish();
        else setIndex(i => i + 1);
    }, [index, steps.length, onFinish]);

    const prev = useCallback(() => setIndex(i => Math.max(0, i - 1)), []);

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onFinish();
            else if (e.key === 'ArrowRight') next();
            else if (e.key === 'ArrowLeft') prev();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, next, prev, onFinish]);

    if (!open || !step) return null;

    const isLast = index === steps.length - 1;

    return (
        <div className="app-tour" role="dialog" aria-modal="true" aria-labelledby="app-tour-title">
            {/* Fondo oscuro con un hueco sobre el elemento; bloquea los clics al resto de la pantalla */}
            {rect ? (
                <div className="app-tour-spotlight" style={{
                    top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2,
                }} />
            ) : (
                <div className="app-tour-backdrop" />
            )}
            <div className="app-tour-blocker" />

            <div
                ref={cardRef}
                className={`app-tour-card app-tour-card-${cardPos?.placement ?? 'center'}`}
                style={cardPos ? { top: cardPos.top, left: cardPos.left } : { visibility: 'hidden' }}
            >
                {cardPos && cardPos.placement !== 'center' && (
                    <span className="app-tour-arrow" style={
                        cardPos.placement === 'left' || cardPos.placement === 'right' ? { top: cardPos.arrow } : { left: cardPos.arrow }
                    } />
                )}
                <div className="d-flex align-items-start gap-2 mb-2">
                    {step.icon && <span className="app-tour-icon"><i className={`bi ${step.icon}`} /></span>}
                    <h6 id="app-tour-title" className="mb-0 flex-grow-1 align-self-center">{step.title}</h6>
                    <button type="button" className="btn-close btn-sm" aria-label="Omitir guía" onClick={onFinish} />
                </div>
                <p className="app-tour-text">{step.content}</p>

                <div className="d-flex align-items-center gap-2">
                    <span className="app-tour-dots" aria-label={`Paso ${index + 1} de ${steps.length}`}>
                        {steps.map((s, i) => <span key={s.key} className={i === index ? 'active' : i < index ? 'seen' : ''} />)}
                    </span>
                    {!isLast && (
                        <button type="button" className="btn btn-link btn-sm text-muted text-decoration-none ms-auto px-1" onClick={onFinish}>
                            Omitir
                        </button>
                    )}
                    <div className={`d-flex gap-2 ${isLast ? 'ms-auto' : ''}`}>
                        {index > 0 && (
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={prev}>Anterior</button>
                        )}
                        <button type="button" className="btn btn-primary btn-sm" onClick={next} autoFocus>
                            {isLast ? 'Finalizar' : index === 0 ? 'Comenzar' : 'Siguiente'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
